import React from 'react'
import { cn } from '../../../lib/utils'
import { Icon } from '../Icon'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../Tooltip'

export type FieldChangeProps = {
  label?: string
  oldValue?: string | null
  newValue?: string | null
  onEdit?: () => void
  handleRevert?: () => void
  className?: string
  size?: 'sm' | 'default'
  error?: { code: string; message: string }
}

export const FieldChange: React.FC<FieldChangeProps> = ({
  label,
  oldValue,
  newValue,
  onEdit,
  handleRevert,
  className,
  size = 'default',
  error,
}) => {
  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex flex-col gap-1 min-w-0 w-full max-w-full overflow-hidden',
          className,
        )}
      >
        {label && (
          <span
            className={cn(
              'font-bold uppercase',
              size === 'default' ? 'text-xs' : 'text-[10px]',
            )}
          >
            {label}
          </span>
        )}
        <div
          className={cn(
            'flex items-center gap-2 min-w-0 w-full max-w-full overflow-hidden',
            size === 'default' ? 'text-sm' : 'text-xs',
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleRevert?.()
                }}
                disabled={!handleRevert}
                className={cn(
                  'bg-muted rounded text-muted-foreground/90 truncate max-w-[120px] hover:bg-muted/80 transition-colors',
                  size === 'default' ? 'p-1 px-2' : 'p-0.5 px-1.5',
                  !handleRevert && 'cursor-default',
                  handleRevert && 'cursor-pointer',
                )}
              >
                {oldValue || '( None )'}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{oldValue || '( None )'}</p>
            </TooltipContent>
          </Tooltip>
          <span className="text-accent-foreground/60">→</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit?.()
                }}
                className={cn(
                  'rounded truncate max-w-[120px]',
                  size === 'default' ? 'p-1 px-2' : 'p-0.5 px-1.5',
                  !error &&
                    'bg-accent text-accent-foreground hover:bg-accent/90',
                  error &&
                    'bg-destructive/10 text-destructive hover:bg-destructive/20 border border-destructive/50',
                )}
                disabled={!onEdit}
              >
                <div className="flex gap-1 items-center min-w-0">
                  <span className="truncate">{newValue || '( None )'}</span>
                  {error && (
                    <Icon
                      className="text-2xs flex-shrink-0"
                      size={size === 'default' ? 12 : 10}
                      name="TriangleAlert"
                    />
                  )}
                  {onEdit && (
                    <Icon
                      className="text-2xs flex-shrink-0"
                      size={size === 'default' ? 12 : 10}
                      name="Pencil"
                    />
                  )}
                </div>
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {!error && <p>{newValue || '( None )'}</p>}
              {error && <p className="text-destructive">{error.message}</p>}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  )
}
